import { useMutation, useQuery } from "@apollo/react-hooks";
import { ChoiceList, Stack, TextField } from "@shopify/polaris";
import { notification } from "antd";
import React from "react";

import { SETTING_VARIABLES } from "../../../constants";
import { useAppContext } from "../../../context";
import { SET_VARIABLE } from "../../../graphql/mutations";
import { GET_VARIABLES } from "../../../graphql/queries";
import { getTeamID, handleError } from "../../../helper";

export default React.forwardRef(function TaskPreview({ setLoading }, ref) {
    const [selected, setSelected] = React.useState(["false"]);

    const { currentUser } = useAppContext();
    const teamId = getTeamID(currentUser);

    const variableKey = React.useMemo(
        () => (teamId ? `${teamId}${SETTING_VARIABLES.TP}` : null),
        [teamId],
    );

    const { data } = useQuery(GET_VARIABLES, {
        variables: {
            filter: {
                search: variableKey,
            },
        },
        skip: !variableKey,
    });

    const [setVariables] = useMutation(SET_VARIABLE, {
        onCompleted: () => {
            notification.success({ message: "Update Task preview success" });
        },
        onError: (err) => {
            notification.error({ message: handleError(err?.toString()) });
        },
    });

    const changed = React.useRef(false);

    React.useEffect(() => {
        const val = data?.variables?.length > 0 ? data.variables[0] : null;

        if (val?.value) {
            const value = String(val.value).trim();

            if (value.length > 0) {
                setSelected([value]);
            }
        }
    }, [data]);

    const handleSubmit = React.useCallback(async () => {
        if (!changed.current) return;

        setLoading(true);
        await setVariables({
            variables: {
                input: {
                    key: variableKey,
                    value: selected?.length > 0 ? selected[0] : "false",
                },
            },
        }).finally(() => {
            setLoading(false);
            changed.current = false;
        });
    }, [variableKey, setVariables, setLoading, selected]);

    const onSelectChange = React.useCallback((value) => {
        setSelected(value);
        changed.current = true;
    }, []);

    React.useImperativeHandle(ref, () => ({
        onSubmit: handleSubmit,
    }));

    return (
        <Stack vertical>
            <ChoiceList
                title="Enable Task Preview"
                choices={[
                    { label: "Enable", value: "true" },
                    { label: "Disable", value: "false" },
                ]}
                selected={selected}
                onChange={onSelectChange}
            />
        </Stack>
    );
});
